import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import type {SettingsSearchController} from "./useSettingsSearch";
import type {SettingsSearchResult, SettingsSearchSection} from "./settingsSearch.types";

export const settingsSearchColors = {
    surface: '#fff',
    subtleSurface: '#f8fafc',
    selected: 'rgba(31, 45, 65, 0.045)',
    selectedBorder: 'rgba(31, 45, 65, 0.12)',
    border: '#dfe3ea',
    text: '#1f2d41',
    muted: '#8792a2',
    icon: '#6f7c8d',
}

const triggerBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    appearance: 'none',
    height: 26,
    py: 0,
    px: 0.9,
    m: 0,
    borderRadius: 1.25,
    bgcolor: 'rgba(0, 133, 155, 0.08)',
    border: '1px solid rgba(0, 133, 155, 0.18)',
    color: '#007f91',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    maxWidth: 'min(160px, 35vw)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    '&:hover': {
        bgcolor: 'rgba(0, 133, 155, 0.12)',
        borderColor: 'rgba(0, 133, 155, 0.28)',
    },
    '&:focus-visible': {
        outline: `2px solid ${settingsSearchColors.muted}`,
        outlineOffset: 2,
    },
}

type SettingsSearchInputProps = {
    search: SettingsSearchController
    onClose?: () => void
    mobile?: boolean
}

export const SettingsSearchInput = ({search, onClose, mobile = false}: SettingsSearchInputProps) => {
    const {t} = useTranslation()

    return (
        <TextField
            autoFocus={search.autoFocus}
            inputRef={search.inputRef}
            fullWidth
            type={mobile ? 'search' : undefined}
            placeholder={search.inputTrigger
                ? t('Search {{scope}}...', {scope: t(search.activeSearchScopeLabel)})
                : t('Search for commands...')}
            value={search.searchInputValue}
            onChange={search.handleSearchInputChange}
            onKeyDown={search.handleKeyDown}
            inputProps={{
                role: 'combobox',
                'aria-autocomplete': 'list',
                'aria-controls': search.resultListboxId,
                'aria-expanded': true,
                'aria-activedescendant': search.selectedResultId
                    ? search.getResultDomId(search.selectedResultId)
                    : undefined,
                inputMode: mobile ? 'search' : undefined,
                enterKeyHint: mobile ? 'search' : undefined,
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start" sx={{gap: 0.75, mr: 0.5}}>
                        <SearchIcon fontSize="small"/>
                        {search.inputTrigger && (
                            <Box
                                component="button"
                                type="button"
                                aria-label={t('Remove {{trigger}} search trigger', {
                                    trigger: search.inputTrigger.trigger,
                                })}
                                tabIndex={-1}
                                onMouseDown={event => event.preventDefault()}
                                onClick={search.removeInputTrigger}
                                sx={triggerBadgeSx}
                            >
                                {search.inputTrigger.trigger}:
                            </Box>
                        )}
                    </InputAdornment>
                ),
                endAdornment: search.canResetSearch || onClose ? (
                    <InputAdornment position="end" sx={{gap: 0.5}}>
                        {search.canResetSearch && (
                            <IconButton
                                aria-label={t('Reset search')}
                                size="small"
                                color="inherit"
                                tabIndex={-1}
                                onMouseDown={event => event.preventDefault()}
                                onClick={search.resetSearch}
                            >
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        )}

                        {onClose && (
                            <Button
                                variant="text"
                                size="tiny"
                                color="inherit"
                                aria-label={t('Close settings search')}
                                tabIndex={-1}
                                onMouseDown={event => event.preventDefault()}
                                onClick={onClose}
                            >
                                {t('Esc')}
                            </Button>
                        )}
                    </InputAdornment>
                ) : undefined,
            }}
            sx={[
                {
                    '& .MuiOutlinedInput-root': {
                        bgcolor: settingsSearchColors.surface,
                        color: settingsSearchColors.text,
                        borderRadius: 2,
                        minHeight: 48,
                        pl: 1.25,
                        '& fieldset': {
                            borderColor: settingsSearchColors.border,
                            borderWidth: 1,
                        },
                        '&:hover fieldset': {
                            borderColor: settingsSearchColors.muted,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                        },
                    },
                    '& .MuiInputBase-input': {
                        py: 1.25,
                        fontSize: 15,
                        fontWeight: 400,
                        '&::-webkit-search-cancel-button': {
                            WebkitAppearance: 'none',
                        },
                        '&::placeholder': {
                            color: settingsSearchColors.muted,
                            opacity: 1,
                        },
                    },
                    '& .MuiInputAdornment-root': {
                        color: settingsSearchColors.icon,
                    },
                },
                Boolean(onClose) && {
                    '& .MuiOutlinedInput-root': {
                        pr: 0.5,
                    },
                },
                Boolean(mobile) && {
                    '& .MuiOutlinedInput-root': {
                        bgcolor: settingsSearchColors.subtleSurface,
                        minHeight: 44,
                    },
                    '& .MuiInputBase-input': {
                        py: 1,
                    },
                },
            ]}
        />
    )
}

type SettingsSearchResultSectionProps = {
    search: SettingsSearchController
    section: SettingsSearchSection
}

const ResultIcon = ({result, executing}: { result: SettingsSearchResult; executing: boolean }) => {
    if (executing) return <CircularProgress size={16} thickness={5} color="inherit"/>
    if (result.section === 'create') return <AddIcon sx={{fontSize: 17}}/>
    if (result.section === 'find') return <SearchIcon sx={{fontSize: 17}}/>
    return <ArrowOutwardIcon sx={{fontSize: 17}}/>
}

export const SettingsSearchResultSection = ({search, section}: SettingsSearchResultSectionProps) => {
    const {t} = useTranslation()
    if (section.results.length === 0) return null

    return (
        <Box>
            <Box sx={{px: 1.5, pb: 0.75}}>
                <Typography
                    variant="body2"
                    sx={{fontWeight: 700, color: settingsSearchColors.text, opacity: 0.65}}
                >
                    {t(section.title)}
                </Typography>
            </Box>

            <List disablePadding>
                {section.results.map(result => {
                    const selected = result.id === search.selectedResultId
                    const executing = result.id === search.executingResultId
                    const firstResult = result.id === search.orderedResults[0]?.id

                    return (
                        <Box
                            key={result.id}
                            sx={[
                                {px: 1, position: 'relative'},
                                Boolean(result.onRemove) && {
                                    '&:hover .settings-search-result-group': {
                                        opacity: 0,
                                        visibility: 'hidden',
                                    },
                                    '&:hover .settings-search-result-remove': {
                                        opacity: 1,
                                        visibility: 'visible',
                                    },
                                },
                            ]}
                        >
                            <ListItemButton
                                id={search.getResultDomId(result.id)}
                                role="option"
                                aria-selected={selected}
                                aria-busy={executing}
                                disabled={executing}
                                tabIndex={selected || (!search.selectedResultId && firstResult) ? 0 : -1}
                                ref={(node: HTMLDivElement | null) => {
                                    search.resultRefs.current[result.id] = node
                                }}
                                onClick={() => search.selectResult(result)}
                                onFocus={() => search.setActiveResult(result.id)}
                                onMouseEnter={() => search.setActiveResult(result.id)}
                                onKeyDown={event => search.handleResultKeyDown(event, result.id)}
                                sx={{
                                    borderRadius: 2,
                                    minHeight: 48,
                                    color: settingsSearchColors.text,
                                    bgcolor: selected ? settingsSearchColors.selected : 'transparent',
                                    border: '2px solid',
                                    borderColor: 'transparent',
                                    p: 0,
                                    '&:hover': {
                                        bgcolor: settingsSearchColors.selected,
                                        borderColor: 'transparent',
                                    },
                                    '&.Mui-focusVisible, &:focus-visible': {
                                        bgcolor: 'transparent',
                                        borderColor: 'primary.main',
                                        outline: 'none',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        width: '100%',
                                        px: 1.25,
                                        py: 0.85,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: settingsSearchColors.border,
                                            bgcolor: settingsSearchColors.subtleSurface,
                                            color: settingsSearchColors.icon,
                                        }}
                                    >
                                        <ResultIcon result={result} executing={executing}/>
                                    </Box>

                                    <Typography
                                        variant="body1"
                                        noWrap
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontSize: 15,
                                            fontWeight: 600,
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {t(result.label)}
                                    </Typography>

                                    <Typography
                                        className="settings-search-result-group"
                                        variant="body2"
                                        noWrap
                                        sx={{
                                            color: settingsSearchColors.muted,
                                            fontWeight: 500,
                                            maxWidth: '40%',
                                            minWidth: 0,
                                        }}
                                    >
                                        {t(result.group)}
                                    </Typography>
                                </Box>
                            </ListItemButton>

                            {result.onRemove && (
                                <Button
                                    className="settings-search-result-remove"
                                    aria-label={t('Remove {{label}} from recent', {label: t(result.label)})}
                                    variant="text"
                                    tabIndex={-1}
                                    onClick={event => {
                                        event.stopPropagation()
                                        result.onRemove?.()
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: 0,
                                        zIndex: 1,
                                        mr: 2,
                                        height: 32,
                                        opacity: 0,
                                        visibility: 'hidden',
                                        transform: 'translateY(-50%)',
                                    }}
                                >
                                    {t('Remove')}
                                </Button>
                            )}
                        </Box>
                    )
                })}
            </List>
        </Box>
    )
}

type SettingsSearchResultsProps = {
    search: SettingsSearchController
    sections: SettingsSearchSection[]
    contentMaxWidth?: number
    scrollMode?: 'contained' | 'page'
}

export const SettingsSearchResults = ({
                                          search,
                                          sections,
                                          contentMaxWidth,
                                          scrollMode = 'contained',
                                      }: SettingsSearchResultsProps) => {
    const {t} = useTranslation()

    return (
        <Stack
            id={search.resultListboxId}
            role="listbox"
            sx={{
                width: '100%',
                height: scrollMode === 'page' ? 'auto' : '100%',
                flex: scrollMode === 'page' ? '0 0 auto' : 1,
                minHeight: 0,
                overflowY: scrollMode === 'page' ? 'visible' : 'auto',
                overflowX: 'hidden',
                pt: 1,
                pb: 1,
                scrollbarColor: `${settingsSearchColors.border} transparent`,
            }}
        >
            <Stack
                gap={1.5}
                sx={{
                    width: '100%',
                    maxWidth: contentMaxWidth,
                    mx: 'auto',
                }}
            >
                {sections.map(section => (
                    <SettingsSearchResultSection key={section.title} search={search} section={section}/>
                ))}

                {search.actionError && (
                    <Typography role="alert" variant="body2" sx={{color: 'error.main', px: 2, py: 1}}>
                        {t('Could not complete this action. Please try again.')}
                    </Typography>
                )}

                {search.providersLoading && (
                    <Box sx={{p: 2}}>
                        <Typography variant="body2" sx={{color: settingsSearchColors.muted}}>
                            {t('Searching {{scope}} for "{{term}}"...', {
                                scope: t(search.activeSearchScopeLabel),
                                term: search.searchInputValue.trim(),
                            })}
                        </Typography>
                    </Box>
                )}

                {search.orderedResults.length === 0 && !search.providersLoading && (
                    <Box sx={{p: 2}}>
                        <Typography variant="body1" sx={{color: settingsSearchColors.text, fontWeight: 600}}>
                            {search.inputTrigger
                                ? search.hasSearchInputValue
                                    ? t('No {{scope}} found', {scope: t(search.activeSearchScopeLabel)})
                                    : t('Search {{scope}}', {scope: t(search.activeSearchScopeLabel)})
                                : t('No settings commands found')}
                        </Typography>
                        <Typography variant="body2" sx={{color: settingsSearchColors.muted}}>
                            {search.inputTrigger
                                ? search.hasSearchInputValue
                                    ? t('Try another name or click the trigger to remove it.')
                                    : t('Type a name to search this list, or click the trigger to remove it.')
                                : t('Try Profiles, Services, or Resources.')}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Stack>
    )
}
